import React, { FC, Fragment } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ImageDataLike } from "gatsby-plugin-image";
import { LinkType } from "../../types";
import MiniTicks from "../miniTicks/MiniTicks";
import { Container } from "../container/Container";
import { useBackgroundColor } from "../../utils";
import { Typography } from "../typography/Typography";
import Stack from "../stack/Stack";
import { Button } from "../button";
import Image from "../image/Image";
import TrustLogos from "../trustLogos/TrustLogos";
import { ArrowLeft, Tick } from "../icon";
import Link from "../link/Link";
import { StyledSection } from "../section/Section";
import Html from "../html/Html";
import {
  StyledFullWidthImage,
  StyledFullWidthImageContainer,
  StyledFullWidthImageOverlay,
} from "../fullWidthImage/FullWidthImage";
import LinkTo from "../linkTo/LinkTo";

// Interface

export interface HeroProps {
  children?: React.ReactNode; // Option children to place components inside i.e service status
  breadcrumb?: LinkType[];
  image?: ImageDataLike | string;
  superTitle?: string;
  heading: string;
  meta?: string;
  subHeading?: string;
  buttons?: React.ReactNode;
  ticks?: string[];
  backgroundColor?: "darkBlue" | "neutral";
  buttonPlacement?: "belowText" | "bottomRight";
  variant?: "primary" | "secondary";
  narrow?: boolean;
  smallText?: string;
  bottomLogos?: {
    logo: ImageDataLike;
    logoLink: LinkType;
  }[];
  topLogos?: {
    logo: ImageDataLike;
    logoLink: LinkType;
  }[];
}

export interface StyledHeroProps {
  backgroundColor?: "darkBlue" | "neutral";
}

export interface HeroButtonsProps {
  button?: LinkType;
  buttonTwo?: LinkType;
}

export interface StyledRightColumnProps {
  bottomAligned?: boolean;
}

export interface StyledBreadcrumbProps {
  multiItem?: boolean;
}

// Styles

const StyledHero = styled.div<StyledHeroProps>`
  ${({ theme: { space, media, borderRadii }, backgroundColor }) => css`
    padding: ${space.fluidLargeToXxLarge} 0;
    ${useBackgroundColor(backgroundColor || "neutral")};
    border-radius: 0 0 ${borderRadii.medium} ${borderRadii.medium};
    position: relative;
    z-index: 1;

    & + ${StyledSection} {
      margin-top: -${borderRadii.medium};

      > *:first-of-type {
        margin-top: ${borderRadii.medium};
      }
    }

    @media (min-width: ${media.large}) {
      & + ${StyledFullWidthImage} {
        margin-top: -${borderRadii.medium};

        ${StyledFullWidthImageOverlay} {
          margin-top: ${borderRadii.medium};
        }
      }
    }
  `};
`;

const StyledContainer = styled(Container)`
  ${({ theme: { space, colors, media, maxMedia }, width, noImage }) => css`
    display: grid;

    @media (max-width: ${maxMedia.medium}) {
      > * + *:not(:empty) {
        margin-top: ${space.medium};
      }
    }

    @media (min-width: ${media.medium}) {
      grid-template-columns: repeat(2, 1fr);
      gap: ${space.medium};

      ${width === "narrow" || noImage
        ? css`
            grid-template-columns: 1fr;
          `
        : ""}

      ${width !== "narrow" && noImage
        ? css`
            > *:first-of-type {
              width: 75%;
            }
          `
        : ""}
    }
  `};
`;

const StyledLeftColumn = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${space.medium};

    > * {
      text-wrap: balance;
    }
  `};
`;

const StyledRightColumn = styled.div<StyledRightColumnProps>`
  ${({ theme: { space, colors, media, maxMedia }, bottomAligned }) => css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${space.medium};

    ${bottomAligned &&
    css`
      @media (max-width: ${maxMedia.medium}) {
        margin-top: 0 !important; // Sorry
      }
    `}

    @media (min-width: ${media.medium}) {
      justify-content: ${bottomAligned ? "flex-end" : "flex-start"};
    }
  `};
`;

const StyledActions = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.xSmall};

    @media (min-width: ${media.medium}) {
      flex-direction: row;
      gap: ${space.small};
    }
  `};
`;

const StyledToggleButtons = styled.div`
  ${({ theme: { media } }) => css`
    @media (min-width: ${media.medium}) {
      width: 360px;
    }
  `};
`;

const StyledDesktopTrustLogos = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    display: flex;
    gap: ${space.medium};
  `};
`;

const StyledDesktopTrustItems = styled(Stack)`
  ${({ theme: { space, maxMedia } }) => css`
    @media (max-width: ${maxMedia.medium}) {
      display: none;
    }
  `};
`;

const StyledSmallText = styled(Html)`
  ${({ theme: { space, maxMedia } }) => css`
    max-width: 300px;

    > * {
      text-wrap: initial;
    }
  `};
`;

const StyledMobileTrustItems = styled(Stack)`
  ${({ theme: { media } }) => css`
    @media (min-width: ${media.medium}) {
      display: none;
    }
  `};
`;

const StyledMobileTrustLogos = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    display: flex;
    gap: ${space.medium};
  `};
`;

const StyledBreadcrumb = styled.nav<StyledBreadcrumbProps>`
  ${({ theme: { space, media, maxMedia }, multiItem }) => css`
    ol {
      display: flex;
      gap: ${space.xxxxxSmall};

      @media (max-width: ${maxMedia.medium}) {
        li:not(:last-of-type) {
          display: none;
        }

        li:last-of-type {
          svg:last-of-type {
            display: none;
          }
          svg:first-of-type {
            display: block;
          }
        }
      }

      @media (min-width: ${media.medium}) {
        ${multiItem
          ? css`
              li:last-of-type {
                svg {
                  display: none;
                }
              }

              li:first-of-type {
                svg:first-of-type {
                  display: none;
                }
              }
            `
          : css`
              li:last-of-type {
                svg:last-of-type {
                  display: none;
                }
              }
            `}
      }
    }
  `};
`;

// JSX

export const HeroButtons: FC<HeroButtonsProps> = ({ buttons, ...rest }) => {
  if (!buttons) return null;
  return (
    <div {...rest}>
      <Stack gap="xSmall">
        <Typography component="p" variant="bodyLargeBold">
          Show:
        </Typography>
        <StyledToggleButtons>{buttons}</StyledToggleButtons>
      </Stack>
    </div>
  );
};

const StyledMobileHeroButtons = styled(HeroButtons)`
  ${({ theme: { space, media } }) => css`
    margin-top: ${space.medium};
    @media (min-width: ${media.medium}) {
      display: none;
    }
  `};
`;

const StyledDesktopHeroButtons = styled(HeroButtons)`
  ${({ theme: { maxMedia } }) => css`
    display: flex;
    flex-direction: column;
    align-content: flex-end;
    align-items: flex-end;

    @media (max-width: ${maxMedia.medium}) {
      display: none;
    }
  `};
`;

export const Hero: FC<HeroProps> = ({
  children,
  breadcrumb,
  image,
  superTitle,
  heading,
  meta,
  subHeading,
  ticks,
  buttons,
  backgroundColor,
  buttonPlacement = "belowText",
  variant = "primary",
  narrow,
  topLogos,
  bottomLogos,
  smallText,
}) => (
  <>
    <StyledHero backgroundColor={backgroundColor}>
      <StyledContainer width={narrow ? "narrow" : "wide"} noImage={!image}>
        <StyledLeftColumn>
          <Stack gap="fluidSmallToMedium">
            {breadcrumb && (
              <StyledBreadcrumb
                aria-label="breadcrumb"
                role="navigation"
                multiItem={breadcrumb.length > 1}
              >
                <ol itemScope itemType="http://schema.org/BreadcrumbList">
                  {breadcrumb?.map((link, index) => (
                    <li
                      key={index}
                      itemProp="itemListElement"
                      itemScope
                      itemType="http://schema.org/ListItem"
                    >
                      <Link url={link?.url} itemProp="item">
                        <ArrowLeft size="small" />{" "}
                        <span itemProp="name">{link?.label}</span>
                      </Link>
                      <meta itemProp="position" content={index + 1} />
                    </li>
                  ))}
                </ol>
              </StyledBreadcrumb>
            )}
            {superTitle && (
              <Typography component="h1" variant="secondaryHeadingMediumBold">
                {superTitle}
              </Typography>
            )}
            {heading && (
              <Typography
                component={!superTitle ? "h1" : "p"}
                variant={
                  variant === "primary" ? "headingXLarge" : "headingLarge"
                }
              >
                {heading}
              </Typography>
            )}
            {meta && (
              <Typography component="p" variant="bodyLarge">
                {meta}
              </Typography>
            )}
            {subHeading && (
              <Typography
                component="p"
                variant={
                  variant === "primary"
                    ? "secondaryHeadingSmallToMedium"
                    : "secondaryHeadingSmall"
                }
              >
                {subHeading}
              </Typography>
            )}
            {ticks && ticks.length > 0 ? (
              <MiniTicks
                items={ticks}
                tickColor={backgroundColor === "neutral" ? "lilac" : "lime"}
              />
            ) : (
              ""
            )}

            {buttons && buttonPlacement === "belowText" ? (
              <StyledActions>{buttons}</StyledActions>
            ) : (
              ""
            )}
          </Stack>
          {(topLogos && topLogos.length > 0) ||
          (bottomLogos && bottomLogos.length > 0) ||
          smallText ? (
            <StyledDesktopTrustItems>
              {(topLogos && topLogos.length > 0) ||
              (bottomLogos && bottomLogos.length > 0) ? (
                <StyledDesktopTrustLogos>
                  {topLogos?.map((topLogo, index) => (
                    <Fragment key={index}>
                      {topLogo.logoLink ? (
                        <LinkTo
                          to={topLogo?.logoLink?.url}
                          title={topLogo?.logoLink?.label}
                          target={topLogo?.logoLink?.target}
                        >
                          <Image
                            key={index}
                            src={topLogo?.logo?.image?.localFile}
                            alt={topLogo?.logo?.image?.altText}
                          />
                        </LinkTo>
                      ) : (
                        <Image
                          key={index}
                          src={topLogo?.logo?.image?.localFile}
                          alt={topLogo?.logo?.image?.altText}
                        />
                      )}
                    </Fragment>
                  ))}
                  {bottomLogos?.map((bottomLogo, index) => (
                    <Fragment key={index}>
                      {bottomLogo.logoLink ? (
                        <LinkTo
                          to={bottomLogo?.logoLink.url}
                          title={bottomLogo?.logoLink.label}
                          target={bottomLogo?.logoLink.target}
                        >
                          <Image
                            key={index}
                            src={bottomLogo?.logo?.image?.localFile}
                            alt={bottomLogo?.logo?.image?.altText}
                          />
                        </LinkTo>
                      ) : (
                        <Image
                          key={index}
                          src={bottomLogo?.logo?.image?.localFile}
                          alt={bottomLogo?.logo?.image?.altText}
                        />
                      )}
                    </Fragment>
                  ))}
                </StyledDesktopTrustLogos>
              ) : (
                ""
              )}
              {smallText && <StyledSmallText>{smallText}</StyledSmallText>}
            </StyledDesktopTrustItems>
          ) : (
            ""
          )}
        </StyledLeftColumn>

        {image ||
        (topLogos && topLogos.length > 0) ||
        (bottomLogos && bottomLogos.length > 0) ||
        smallText ? (
          <StyledRightColumn bottomAligned={buttonPlacement === "bottomRight"}>
            {image && <Image roundedCorners border src={image} alt={heading} />}
            {buttons && buttonPlacement === "bottomRight" ? (
              <StyledDesktopHeroButtons buttons={buttons} />
            ) : (
              ""
            )}
            {(topLogos && topLogos.length > 0) ||
            (bottomLogos && bottomLogos.length > 0) ? (
              <StyledMobileTrustItems>
                <StyledMobileTrustLogos>
                  {topLogos?.map((topLogo, index) => (
                      <Fragment key={index}>
                      {topLogo.logoLink ? (
                        <LinkTo
                          to={topLogo.logoLink.url}
                          title={topLogo.logoLink.label}
                          target={topLogo.logoLink.target}
                        >
                          <Image
                            key={index}
                            src={topLogo.logo?.image?.localFile}
                            alt={topLogo.logo?.image?.altText}
                          />
                        </LinkTo>
                      ) : (
                        <Image
                          key={index}
                          src={topLogo.logo?.image?.localFile}
                          alt={topLogo.logo?.image?.altText}
                        />
                      )}
                    </Fragment>
                  ))}
                </StyledMobileTrustLogos>
                {smallText && <StyledSmallText>{smallText}</StyledSmallText>}

                {bottomLogos && bottomLogos.length > 0
                  ? bottomLogos?.map((bottomLogo, index) => (
                      <StyledMobileTrustLogos key={index}>
                        {bottomLogo.logoLink ? (
                          <LinkTo
                            to={bottomLogo?.logoLink?.url}
                            title={bottomLogo?.logoLink?.label}
                            target={bottomLogo?.logoLink?.target}
                          >
                            <Image
                              key={index}
                              src={bottomLogo?.logo?.image?.localFile}
                              alt={bottomLogo?.logo?.image?.altText}
                            />
                          </LinkTo>
                        ) : (
                          <Image
                            key={index}
                            src={bottomLogo?.logo?.image?.localFile}
                            alt={bottomLogo?.logo?.image?.altText}
                          />
                        )}
                      </StyledMobileTrustLogos>
                    ))
                  : ""}
              </StyledMobileTrustItems>
            ) : (
              ""
            )}
          </StyledRightColumn>
        ) : (
          ""
        )}

        {children}
      </StyledContainer>
    </StyledHero>
    {buttons && buttonPlacement === "bottomRight" ? (
      <StyledContainer width="wide">
        <StyledMobileHeroButtons buttons={buttons} />
      </StyledContainer>
    ) : (
      ""
    )}
  </>
);

export default Hero;
